import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    getActiveStoreContext,
    parseSlugFromPath,
    readCachedStoreContext,
} from '../../lib/storefront/storeBootstrap';
import {
    getPlatformDomain,
    parseCustomDomainFromHost,
    parseSubdomainFromHost,
    RESERVED_STORE_SUBDOMAINS,
} from '../../lib/storefront/storefrontHost';
import { SUPPORTED_LANGUAGES } from '../../config/languages';

function stripPort(host = '') {
    return String(host).trim().toLowerCase().split(':')[0];
}

/** Brand shared hosts like zilvix.quazbit.com — not merchant subdomains. */
function isReservedBrandHostname(hostname, platformDomain) {
    const host = stripPort(hostname);
    const domain = String(platformDomain || '').trim().toLowerCase();
    if (!host || !domain || domain === 'localhost') return false;
    const suffix = `.${domain}`;
    if (!host.endsWith(suffix)) return false;
    const sub = host.slice(0, -suffix.length);
    return Boolean(sub) && !sub.includes('.') && RESERVED_STORE_SUBDOMAINS.has(sub);
}

/**
 * Keep /s/{org}/{store} in the URL on shared hosts (localhost, workers.dev, pages.dev).
 * Do not rewrite merchant subdomains, custom domains, or reserved brand hosts from a stale session cache.
 */
export default function StorePathRedirect() {
    const { pathname, search, hash } = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const misplaced = pathname.match(/^\/([a-z]{2})(\/s\/[^/]+\/[^/]+)(?:\/[a-z]{2})?(.*)$/i);
        if (misplaced && SUPPORTED_LANGUAGES.includes(misplaced[1].toLowerCase())) {
            navigate(`${misplaced[2]}/${misplaced[1]}${misplaced[3] || ''}${search}${hash}`, { replace: true });
            return;
        }

        if (parseSlugFromPath(pathname)) return;

        const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
        const platformDomain = getPlatformDomain();
        if (parseSubdomainFromHost(hostname, platformDomain)) return;
        if (parseCustomDomainFromHost(hostname, platformDomain)) return;
        // zilvix.quazbit.com — require an explicit /s/... path; don't inject a stale cache.
        if (isReservedBrandHostname(hostname, platformDomain)) return;

        const ctx = getActiveStoreContext() || readCachedStoreContext();
        // Bootstrap already resolved via host — never force a path URL afterward.
        if (ctx?.routingMode === 'subdomain' || ctx?.routingMode === 'custom_domain') return;

        const orgSlug = String(ctx?.organization?.slug || process.env.REACT_APP_ORG_SLUG || '').trim();
        const storeSlug = String(ctx?.store?.slug || process.env.REACT_APP_STORE_SLUG || '').trim();
        if (!orgSlug || !storeSlug) return;

        const rest = pathname === '/' ? '' : pathname;
        if (rest.toLowerCase().includes('/s/')) return;

        navigate(`/s/${encodeURIComponent(orgSlug)}/${encodeURIComponent(storeSlug)}${rest}${search}${hash}`, {
            replace: true,
        });
    }, [pathname, search, hash, navigate]);

    return null;
}
